import { TopVendorItem } from '../../api/types';

interface TopVendorsChartProps {
  data?: TopVendorItem[];
  isLoading?: boolean;
}

const DEFAULT_VENDORS: TopVendorItem[] = [
  { vendor_name: 'GitHub Inc.', total_amount: '1240.00', currency: 'EUR', percentage: 28.5, invoices_count: 8 },
  { vendor_name: 'Amazon Web Services', total_amount: '3400.00', currency: 'EUR', percentage: 42.0, invoices_count: 4 },
  { vendor_name: 'Google Cloud', total_amount: '1850.00', currency: 'EUR', percentage: 16.5, invoices_count: 6 },
  { vendor_name: 'Vercel', total_amount: '680.00', currency: 'EUR', percentage: 8.0, invoices_count: 3 },
  { vendor_name: 'Others', total_amount: '420.00', currency: 'EUR', percentage: 5.0, invoices_count: 5 },
];

const COLORS = ['#114FEE', '#16EA9E', '#6C11EE', '#F59E0B', '#64748B'];

export default function TopVendorsChart({
  data = DEFAULT_VENDORS,
  isLoading = false,
}: TopVendorsChartProps) {
  const rawVendors = data && data.length > 0 ? data : DEFAULT_VENDORS;

  const vendors = rawVendors.map((v) => {
    const vendorName =
      typeof v.vendor_name === 'string'
        ? v.vendor_name
        : (v && typeof v === 'object' && 'name' in v && typeof (v as { name: unknown }).name === 'string')
        ? (v as { name: string }).name
        : 'Unknown Supplier';

    const percentage =
      typeof v.percentage === 'number' && !isNaN(v.percentage)
        ? v.percentage
        : parseFloat(String(v.percentage || '0')) || 0;

    const totalAmount =
      typeof v.total_amount === 'number'
        ? v.total_amount
        : parseFloat(String(v.total_amount || '0')) || 0;

    return {
      vendor_name: vendorName,
      percentage,
      total_amount: String(totalAmount),
    };
  });

  return (
    <div className="col-span-12 lg:col-span-6 rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-5 sm:p-7 shadow-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-brand-green" />
            <h3 className="text-base sm:text-lg font-bold text-brand-navy dark:text-white">
              Vendor Concentration
            </h3>
          </div>
          <p className="text-xs text-brand-muted dark:text-bodydark mt-0.5">
            Top spending suppliers by share of total invoice volume
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="h-48 flex items-center justify-center text-sm text-brand-soft">
          <div className="w-6 h-6 border-2 border-brand-green border-t-transparent rounded-full animate-spin mr-2" />
          Loading vendor data…
        </div>
      ) : (
        <div className="space-y-3.5 pt-2">
          {vendors.slice(0, 5).map((v, idx) => {
            const color = COLORS[idx % COLORS.length];
            const displayAmt = parseFloat(v.total_amount) || 0;
            const displayPct = v.percentage || 0;

            return (
              <div key={idx} className="space-y-1.5">
                <div className="flex items-center justify-between text-xs font-mono">
                  <div className="flex items-center gap-2 truncate">
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: color }}
                    />
                    <span className="font-semibold text-brand-navy dark:text-white truncate">
                      {v.vendor_name}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 font-semibold">
                    <span className="text-brand-soft dark:text-bodydark2">
                      €{displayAmt.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                    <span className="text-brand-navy dark:text-white w-10 text-right">
                      {displayPct.toFixed(1)}%
                    </span>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="w-full h-2 rounded-full bg-gray-100 dark:bg-strokedark overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min(100, Math.max(2, displayPct))}%`,
                      backgroundColor: color,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
